import React, { useEffect, useRef, useState } from 'react';
import { SMSPageLayout } from './SMSPageLayout';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../common/Button';
import { AddContactsModal } from '../contacts/AddContactsModal';
import { ImportContactsModal } from '../contacts/ImportContactsModal';
import { AllContactsModal } from '../contacts/AllContactsModal';
import { GroupsModal } from '../contacts/GroupsModal';
import { PersonalizeModal } from './PersonalizeModal';
import { getUserSenderIds, SenderIdDto } from '../../services/SenderIdService';
import { sendCampaignSms } from '../../services/SmsService';
import { fetchUserTemplates, TemplateRecord } from '../../services/TemplateService';
import { toValidZimbabwePhone, ZIMBABWE_PHONE_HINT } from '../../utils/phoneNumber';
import {
  PersonalizedRow,
  PersonalizePreviewRow,
  PersonalizeParseResult
} from '../../utils/excelPersonalize';
import { applyMergeFields, toVariableToken } from '../../utils/messagePersonalize';

export const SMSCampaignPage: React.FC = () => {
  const [message, setMessage] = useState('');
  const [messageOverview, setMessageOverview] = useState('');
  const [recipients, setRecipients] = useState<string[]>([]);
  const [smsCount, setSmsCount] = useState(1);
  const [campaignName, setCampaignName] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [addToTemplate, setAddToTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [isAddContactsModalOpen, setIsAddContactsModalOpen] = useState(false);
  const [isImportContactsModalOpen, setIsImportContactsModalOpen] = useState(false);
  const [isPersonalizeModalOpen, setIsPersonalizeModalOpen] = useState(false);
  const [isAllContactsModalOpen, setIsAllContactsModalOpen] = useState(false);
  const [isGroupsModalOpen, setIsGroupsModalOpen] = useState(false);
  const [messageGoal, setMessageGoal] = useState('Promotional Message');
  const [tone, setTone] = useState('Friendly');
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedVariables, setSelectedVariables] = useState<string[]>([]);
  const [senderId, setSenderId] = useState<string>('');
  const [availableSenderIds, setAvailableSenderIds] = useState<SenderIdDto[]>([]);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [templates, setTemplates] = useState<TemplateRecord[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [isLoadingTemplates, setIsLoadingTemplates] = useState<boolean>(false);
  const [modal, setModal] = useState<{
    type: 'success' | 'error';
    title: string;
    message: string;
    subMessage?: string;
  } | null>(null);
  const [personalizeColumns, setPersonalizeColumns] = useState<string[]>([]);
  const [personalizeRows, setPersonalizeRows] = useState<PersonalizedRow[]>([]);
  const [personalizePreview, setPersonalizePreview] = useState<PersonalizePreviewRow[]>([]);
  const [personalizeFileName, setPersonalizeFileName] = useState('');
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null);

  const navigate = useNavigate();
  const location = useLocation();

  function getCurrentUserId(): string | null {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken');
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      return payload?.id || null;
    } catch (_) {
      return null;
    }
  }

  // Handle data returned from preview page
  React.useEffect(() => {
    if (location.state) {
      const previewData = location.state as {
        message: string;
        recipients: string[];
        campaignName: string;
        scheduledDate: string;
        scheduledTime: string;
        smsCount: number;
        personalizeColumns?: string[];
        personalizeRows?: PersonalizedRow[];
        personalizePreview?: PersonalizePreviewRow[];
        personalizeFileName?: string;
      };

      setMessage(previewData.message);
      setRecipients(previewData.recipients);
      setCampaignName(previewData.campaignName);
      setScheduledDate(previewData.scheduledDate);
      setScheduledTime(previewData.scheduledTime);
      setSmsCount(previewData.smsCount);
      if (previewData.personalizeColumns) {
        setPersonalizeColumns(previewData.personalizeColumns);
      }
      if (previewData.personalizeRows) {
        setPersonalizeRows(previewData.personalizeRows);
      }
      if (previewData.personalizePreview) {
        setPersonalizePreview(previewData.personalizePreview);
      }
      if (previewData.personalizeFileName) {
        setPersonalizeFileName(previewData.personalizeFileName);
      }
    }
  }, [location.state]);

  useEffect(() => {
    const init = async () => {
      const uid = getCurrentUserId();
      if (!uid) return;
      try {
        const list = await getUserSenderIds(uid);
        setAvailableSenderIds(list);
        if (list.length && !senderId) setSenderId(list[0].senderId);
      } catch (_) {
        // ignore
      }
    };
    init();
  }, []);

  // Fetch user templates on component mount
  useEffect(() => {
    const loadTemplates = async () => {
      setIsLoadingTemplates(true);
      try {
        const data = await fetchUserTemplates();
        setTemplates(data);
      } catch (error) {
        console.error('Failed to load templates:', error);
        // Silently fail - dropdown will just be empty
      } finally {
        setIsLoadingTemplates(false);
      }
    };
    loadTemplates();
  }, []);

  // Handle template selection
  const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const templateId = e.target.value;
    setSelectedTemplateId(templateId);
    
    if (templateId) {
      const selectedTemplate = templates.find(t => t.id === templateId);
      if (selectedTemplate) {
        setMessage(selectedTemplate.content || '');
      }
    } else {
      // If "Choose template" is selected, optionally clear the message
      // Or leave it as is to allow user to keep editing
    }
  };

  const handleRemoveRecipient = (numberToRemove: string) => {
    setRecipients(recipients.filter(number => number !== numberToRemove));
    setPersonalizeRows(personalizeRows.filter(row => row.phone !== numberToRemove));
  };

  const buildRecipientMessages = () => {
    if (personalizeRows.length === 0) {
      return undefined;
    }

    const fromFile = personalizeRows.map((row) => ({
      phone: row.phone,
      body: applyMergeFields(message, row.fields),
      fields: row.fields
    }));
    const filePhones = new Set(personalizeRows.map((row) => row.phone));
    const extras = recipients
      .filter((phone) => !filePhones.has(phone))
      .map((phone) => ({
        phone,
        body: applyMergeFields(message, {}),
        fields: {} as Record<string, string>
      }));

    return [...fromFile, ...extras];
  };

  const handlePreviewClick = () => {
    const recipientMessages = buildRecipientMessages();
    navigate('/sms/preview', {
      state: {
        message,
        recipients,
        campaignName,
        scheduledDate,
        scheduledTime,
        smsCount,
        senderId,
        recipientMessages,
        personalizeColumns,
        personalizeRows,
        personalizePreview,
        personalizeFileName
      }
    });
  };

  const handlePersonalizeClick = () => {
    setIsPersonalizeModalOpen(true);
  };

  const handlePersonalizeSave = (parsed: PersonalizeParseResult) => {
    const uniquePhones: string[] = [];
    parsed.rows.forEach((row) => {
      if (!uniquePhones.includes(row.phone)) {
        uniquePhones.push(row.phone);
      }
    });

    setPersonalizeColumns(parsed.columns);
    setPersonalizeRows(parsed.rows);
    setPersonalizePreview(parsed.previewRows);
    setPersonalizeFileName(parsed.fileName);
    setRecipients(uniquePhones);

    if (parsed.skipped > 0) {
      setModal({
        type: 'error',
        title: 'Some rows were skipped',
        message: `${parsed.rows.length} valid row(s) loaded. ${parsed.skipped} row(s) had an invalid mobile number.`,
        subMessage: ZIMBABWE_PHONE_HINT
      });
    }
  };

  const handleInsertVariable = (token: string) => {
    const textArea = messageTextareaRef.current;
    if (!textArea) {
      setMessage((current) => current + token);
      return;
    }
    const start = textArea.selectionStart;
    const end = textArea.selectionEnd;
    const next = message.substring(0, start) + token + message.substring(end);
    setMessage(next);
    requestAnimationFrame(() => {
      textArea.focus();
      const cursor = start + token.length;
      textArea.setSelectionRange(cursor, cursor);
    });
  };

  const messageVariables = personalizeColumns.map((column) => ({
    label: column,
    value: toVariableToken(column)
  }));

  const handleAudienceContacts = (contacts: string[]) => {
    const newRecipients = [...recipients];
    const invalidNumbers: string[] = [];

    contacts.forEach(contact => {
      const cleaned = toValidZimbabwePhone(contact);
      if (cleaned) {
        if (!newRecipients.includes(cleaned)) {
          newRecipients.push(cleaned);
        }
      } else {
        invalidNumbers.push(contact);
      }
    });

    setRecipients(newRecipients);

    if (invalidNumbers.length > 0) {
      setModal({
        type: 'error',
        title: 'Invalid numbers',
        message: `${invalidNumbers.length} invalid phone number(s) were not added.`,
        subMessage: ZIMBABWE_PHONE_HINT
      });
    }
  };

  const handleGenerateMessage = async () => {
    if (!messageOverview) {
      // Show error if no message overview
      const modal = document.createElement('div');
      modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
      modal.innerHTML = `
        <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div class="flex items-center justify-center mb-4">
            <svg class="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 class="text-xl font-semibold text-center mb-2">Message Overview Required</h3>
          <p class="text-gray-600 text-center mb-6">Please provide an overview of your message to help the AI generate the best content.</p>
          <div class="flex justify-center">
            <button 
              onclick="this.closest('.fixed').remove()"
              class="px-6 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#F4511E] transition-colors duration-200"
            >
              Close
            </button>
          </div>
        </div>
      `;
      document.body.appendChild(modal);
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          modal.remove();
        }
      });
      return;
    }

    setIsGenerating(true);

    try {
      // Simulate AI message generation (replace with actual API call)
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Example of AI-generated message based on overview, variables, tone, and goal
      let generatedMessage = '';
      
      // Add greeting based on tone
      if (tone === 'Friendly') {
        generatedMessage += 'Hi there! 👋\n\n';
      } else if (tone === 'Professional') {
        generatedMessage += 'Dear valued customer,\n\n';
      } else if (tone === 'Casual') {
        generatedMessage += 'Hey! 👋\n\n';
      } else {
        generatedMessage += 'Greetings,\n\n';
      }

      // Add main message based on overview
      generatedMessage += messageOverview + '\n\n';

      // Add variables if selected
      if (selectedVariables.length > 0) {
        generatedMessage += 'Personalized details:\n';
        selectedVariables.forEach(variable => {
          generatedMessage += `- ${variable}\n`;
        });
        generatedMessage += '\n';
      }

      // Add closing based on goal and tone
      if (messageGoal === 'Promotional Message') {
        generatedMessage += 'Don\'t miss out on this amazing offer! 🎉';
      } else if (messageGoal === 'Informational Update') {
        generatedMessage += 'Stay tuned for more updates! 📢';
      } else if (messageGoal === 'Event Reminder') {
        generatedMessage += 'We look forward to seeing you there! 📅';
      } else {
        generatedMessage += 'Thank you for your attention! 🙏';
      }

      // Update the message
      setMessage(generatedMessage);

      // Show success modal
      const modal = document.createElement('div');
      modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
      modal.innerHTML = `
        <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div class="flex items-center justify-center mb-4">
            <svg class="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 class="text-xl font-semibold text-center mb-2">Message Generated!</h3>
          <p class="text-gray-600 text-center mb-6">The AI has created a message based on your overview, tone, and goals.</p>
          <div class="flex justify-center">
            <button 
              onclick="this.closest('.fixed').remove()"
              class="px-6 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#F4511E] transition-colors duration-200"
            >
              Close
            </button>
          </div>
        </div>
      `;
      document.body.appendChild(modal);
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          modal.remove();
        }
      });
    } catch (error) {
      console.error('Error generating message:', error);
      // Show error modal
      const modal = document.createElement('div');
      modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
      modal.innerHTML = `
        <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div class="flex items-center justify-center mb-4">
            <svg class="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 class="text-xl font-semibold text-center mb-2">Error Generating Message</h3>
          <p class="text-gray-600 text-center mb-6">There was an error generating your message. Please try again.</p>
          <div class="flex justify-center">
            <button 
              onclick="this.closest('.fixed').remove()"
              class="px-6 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#F4511E] transition-colors duration-200"
            >
              Close
            </button>
          </div>
        </div>
      `;
      document.body.appendChild(modal);
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          modal.remove();
        }
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleVariableSelect = (variable: string) => {
    if (!selectedVariables.includes(variable)) {
      setSelectedVariables([...selectedVariables, variable]);
    }
  };

  const handleSendCampaign = async () => {
    if (!message.trim()) {
      setModal({ type: 'error', title: 'Message required', message: 'Please enter a message.' });
      return;
    }
    if (recipients.length === 0) {
      setModal({ type: 'error', title: 'No recipients', message: 'Please add at least one recipient.' });
      return;
    }
    setIsSending(true);
    try {
      const resp = await sendCampaignSms({ 
        senderId, 
        body: message, 
        recipients,
        recipientMessages: buildRecipientMessages(),
        campaignName: campaignName || undefined,
        scheduledDate: scheduledDate || undefined,
        scheduledTime: scheduledTime || undefined
      });
      setModal({
        type: 'success',
        title: resp.isScheduled ? 'Campaign scheduled' : 'Campaign queued',
        message: `${resp.queued} recipient(s) queued for sending.`,
        subMessage: resp.invalid.length > 0 ? `${resp.invalid.length} number(s) were invalid and skipped.` : undefined
      });
      // Optionally reset form or navigate
      // setMessage(''); setRecipients([]); setCampaignName('');
    } catch (e: any) {
      setModal({ type: 'error', title: 'Failed to send campaign', message: e?.message || 'Failed to send campaign SMS' });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <SMSPageLayout title="SMS Campaign">
      <div className="space-y-6 bg-white rounded-lg p-6">
        {/* Campaign Name and Schedule Row */}
        <div className="flex items-start space-x-4">
          <div className="w-1/2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Campaign Name
            </label>
            <input
              type="text"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder="Enter campaign name"
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
            />
          </div>
          <div className="w-1/4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Schedule Date
            </label>
            <input
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
            />
          </div>
          <div className="w-1/4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Schedule Time
            </label>
            <input
              type="time"
              value={scheduledTime}
              onChange={(e) => setScheduledTime(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
            />
          </div>
        </div>

        {/* Sender ID and Audience */}
        <div className="flex items-start space-x-4 mb-6">
          <div className="w-1/2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Sender I.D.
            </label>
            <div className="flex items-end space-x-4">
              <select 
                className="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                value={senderId}
                onChange={(e) => setSenderId(e.target.value)}
              >
                {availableSenderIds.length === 0 && <option value="">Select sender ID</option>}
                {availableSenderIds.map(s => (
                  <option key={s.id} value={s.senderId}>{s.senderId}</option>
                ))}
              </select>
              <a 
                href="#" 
                className="text-[#FF5722] text-sm hover:underline mb-2 whitespace-nowrap"
              >
                Apply for new sender I.D.
              </a>
            </div>
          </div>

          <div className="w-1/2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Choose your audience
            </label>
            <div className="flex space-x-4">
              <button 
                onClick={() => setIsAddContactsModalOpen(true)}
                className="flex flex-1 flex-col items-center justify-center h-24 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6 text-gray-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                <span className="text-xs text-gray-600 text-center">Add Contact</span>
              </button>

              <button 
                onClick={() => setIsImportContactsModalOpen(true)}
                className="flex flex-1 flex-col items-center justify-center h-24 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6 text-gray-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-xs text-gray-600 text-center">Import Contacts</span>
              </button>

              <button 
                onClick={() => setIsAllContactsModalOpen(true)}
                className="flex flex-1 flex-col items-center justify-center h-24 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6 text-gray-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="text-xs text-gray-600 text-center">All Contacts</span>
              </button>

              <button 
                onClick={() => setIsGroupsModalOpen(true)}
                className="flex flex-1 flex-col items-center justify-center h-24 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6 text-gray-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <span className="text-xs text-gray-600 text-center">Group</span>
              </button>
            </div>
          </div>
        </div>

        {/* Recipients List Box */}
        <div className="mt-4 border border-gray-200 rounded-md bg-white p-4">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-medium text-gray-700">Recipients</h3>
            <span className="text-sm text-gray-500">
              {personalizeRows.length > recipients.length
                ? `${recipients.length} number(s) · ${personalizeRows.length} personalized row(s)`
                : `${recipients.length} number(s)`}
            </span>
          </div>
          {recipients.length > 0 ? (
            <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
              {recipients.map((number) => (
                <div 
                  key={number} 
                  className="inline-flex items-center space-x-1 bg-gray-50 px-2 py-1 rounded-md"
                >
                  <span className="text-sm text-gray-600">{number}</span>
                  <button
                    onClick={() => handleRemoveRecipient(number)}
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                    aria-label={`Remove ${number}`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-sm text-gray-500">
              No recipients added yet
            </div>
          )}
        </div>

        {personalizePreview.length > 0 && (
          <div className="mt-4 border border-gray-200 rounded-md bg-white p-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-gray-700">Personalize preview</h3>
              <span className="text-sm text-gray-500">
                First {personalizePreview.length} row(s) from {personalizeFileName}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    {personalizeColumns.map((column) => (
                      <th
                        key={column}
                        className="px-3 py-2 text-left font-medium text-gray-700 whitespace-nowrap"
                      >
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {personalizePreview.map((row, index) => (
                    <tr key={index} className="border-t border-gray-100">
                      {personalizeColumns.map((column, colIndex) => (
                        <td
                          key={`${index}-${column}`}
                          className={`px-3 py-2 text-gray-600 whitespace-nowrap ${
                            colIndex === 0 && !row.isValid ? 'text-red-600' : ''
                          }`}
                        >
                          {row.values[colIndex] || ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Message and Template Section */}
        <div className="grid grid-cols-2 gap-6">
          {/* Message Area */}
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Add SMS message
            </label>

            {/* Templates Dropdown */}
            <div className="mb-3">
              <select 
                value={selectedTemplateId}
                onChange={handleTemplateChange}
                disabled={isLoadingTemplates}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Choose template</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
              {isLoadingTemplates && (
                <p className="text-xs text-gray-500 mt-1">Loading templates...</p>
              )}
            </div>

            <textarea
              ref={messageTextareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
            />

            {/* Preview Campaign Button */}
            <div className="mt-4">
              <button
                onClick={handlePreviewClick}
                className="w-full bg-[#FF5722] text-white px-6 py-3 rounded-md hover:bg-[#F4511E] transition-colors duration-200 font-medium flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span>Preview Campaign</span>
              </button>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-4">
            <div className="flex items-end space-x-4">
              <div className="w-1/2">
                <button
                  type="button"
                  onClick={handlePersonalizeClick}
                  className="w-full bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E] transition-colors duration-200 font-medium"
                >
                  Personalize
                </button>
              </div>
              <div className="w-1/2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message Variables
                </label>
                <select 
                  disabled={messageVariables.length === 0}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  onChange={(e) => {
                    if (e.target.value) {
                      handleInsertVariable(e.target.value);
                    }
                    e.target.value = '';
                  }}
                >
                  <option value="">
                    {messageVariables.length === 0 ? 'No variables yet' : 'Select variable'}
                  </option>
                  {messageVariables.map((variable) => (
                    <option key={variable.value} value={variable.value}>
                      {variable.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {personalizeFileName && (
              <p className="text-xs text-gray-500 truncate">
                Using columns from {personalizeFileName}
              </p>
            )}

                 {/* Total Symbols Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total characters in SMS message
                  </label>
                  <div className="flex items-center justify-between p-2 border border-gray-300 rounded-md bg-gray-50">
                    <span className="text-sm text-gray-600">Characters used:</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900">{message.length}</span>
                      <span className="text-sm text-gray-500">/ 160</span>
                      <span className="text-xs text-gray-500 ml-2">
                        ({Math.ceil(message.length / 160)} SMS)
                      </span>
                    </div>
                  </div>
                </div>            
          </div>
        </div>

        {/* AI SMS Assistant Section */}
        {false && ( 
        <div className="border border-gray-200 rounded-md bg-white p-6 space-y-4 mt-6">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-bold">AI SMS Assistant</h3>
            <svg className="w-5 h-5 text-[#FF5722]" fill="currentColor" viewBox="0 0 24 24">
              <path d="M11.47 3.84a.75.75 0 011.06 0l8.69 8.69a.75.75 0 101.06-1.06l-8.689-8.69a2.25 2.25 0 00-3.182 0l-8.69 8.69a.75.75 0 001.061 1.06l8.69-8.69z" />
              <path d="M12 5.432l8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 01-.75-.75v-4.5a.75.75 0 00-.75-.75h-3a.75.75 0 00-.75.75V21a.75.75 0 01-.75.75H5.625a1.875 1.875 0 01-1.875-1.875v-6.198a2.29 2.29 0 00.091-.086L12 5.43z" />
            </svg>
          </div>
          
          <p className="text-sm text-gray-600">
            Generate personalized SMS messages in seconds.
          </p>

          {/* Message Overview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message Overview
            </label>
            <textarea
              value={messageOverview}
              onChange={(e) => setMessageOverview(e.target.value)}
              placeholder="Describe what you want to communicate in your message..."
              rows={4}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
            />
          </div>

          {/* Message Goal Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message Goal
            </label>
            <select 
              value={messageGoal}
              onChange={(e) => setMessageGoal(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
            >
              <option>Promotional Message</option>
              <option>Informational Update</option>
              <option>Event Reminder</option>
              <option>Customer Service</option>
            </select>
          </div>

          {/* Tone Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tone
            </label>
            <select 
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
            >
              <option>Friendly</option>
              <option>Professional</option>
              <option>Casual</option>
              <option>Formal</option>
            </select>
          </div>

          {/* Selected Variables Display */}
          {selectedVariables.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Selected Variables
              </label>
              <div className="flex flex-wrap gap-2">
                {selectedVariables.map((variable, index) => (
                  <div
                    key={index}
                    className="bg-gray-100 px-3 py-1 rounded-full text-sm flex items-center space-x-1"
                  >
                    <span>{variable}</span>
                    <button
                      onClick={() => setSelectedVariables(selectedVariables.filter(v => v !== variable))}
                      className="text-gray-500 hover:text-red-500"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Message Variables Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message Variables
            </label>
            <select 
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
              onChange={(e) => {
                if (e.target.value) {
                  handleVariableSelect(e.target.value);
                  e.target.value = ''; // Reset selection
                }
              }}
            >
              <option value="">Select variable</option>
              <option value="{NAME}">Name</option>
              <option value="{SURNAME}">Surname</option>
              <option value="{PHONE}">Phone number</option>
              <option value="{EMAIL}">Email</option>
              <option value="{BIRTHDAY}">Date</option>
              <option value="{CUSTM1}">Custom field (1)</option>
              <option value="{CUSTM2}">Custom field (2)</option>
              <option value="{CUSTM3}">Custom field (3)</option>
              <option value="{UNSUBSCRIBE}">Unsubscribe</option>
              <option value="{URL}">URL</option>
              <option value="{RANDOM}">Random</option>
              <option value="{NUMBER1}">Number (1 digit)</option>
              <option value="{NUMBER3}">Number (3 digits)</option>
              <option value="{NUMBER6}">Number (6 digits)</option>
              <option value="{SPIN=HELLO,HEY,HI}">Spin (Hello/Hey/Hi)</option>
            </select>
          </div>

          {/* Generate Button */}
          <button 
            onClick={handleGenerateMessage}
            disabled={isGenerating}
            className={`w-full ${isGenerating ? 'bg-gray-400' : 'bg-[#1a1a1a] hover:bg-black'} text-white px-4 py-2 rounded-md transition-colors duration-200 flex items-center justify-center space-x-2`}
          >
            {isGenerating ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Generating...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span>Generate Message</span>
              </>
            )}
          </button>

          {/* AI Generated Message Area */}
          <div className="mt-4">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Your AI-generated SMS will appear here..."
              rows={4}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent bg-gray-50"
            />
          </div>

          {/* Add to Template Checkbox and SMS Count */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="addToTemplate"
                checked={addToTemplate}
                onChange={(e) => setAddToTemplate(e.target.checked)}
                className="h-4 w-4 text-[#FF5722] border-gray-300 rounded focus:ring-[#FF5722]"
              />
              <label htmlFor="addToTemplate" className="text-sm text-gray-700">
                Add to Template
              </label>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">SMS Count:</span>
              <span className="text-sm font-medium text-gray-900">1</span>
            </div>
          </div>

          {/* Template Name Input - Only shows when checkbox is checked */}
          {addToTemplate && (
            <div className="animate-fade-in">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name of Template
              </label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Enter template name"
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
              />
            </div>
          )}
        </div>
        )}
        {/* Campaign Summary Section - Moved here */}
        {false && ( 
        <div className="border-t border-b border-gray-200 py-4 my-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Campaign Summary</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Campaign Name:</span>
                <span className="text-sm font-medium text-gray-900">{campaignName || 'Untitled'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Recipients:</span>
                <span className="text-sm font-medium text-gray-900">{recipients.length} contact(s)</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Schedule:</span>
                <span className="text-sm font-medium text-gray-900">
                  {scheduledDate && scheduledTime 
                    ? `${scheduledDate} at ${scheduledTime}`
                    : 'Not scheduled'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total SMS Cost:</span>
                <span className="text-sm font-medium text-[#FF5722]">
                  {recipients.length * smsCount} credits
                </span>
              </div>
            </div>
          </div>
        </div>
        )}
        {/* Action Buttons */}
        {false && (
        <div className="flex justify-end space-x-4">
          <Button 
            to="/sms/preview"
            className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200"
          >
            Preview SMS
          </Button>
          <button 
            onClick={handleSendCampaign}
            disabled={isSending}
            className="bg-[#FF5722] text-white px-6 py-2 rounded-md hover:bg-[#F4511E] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSending ? 'Sending...' : 'Send Campaign'}
          </button>
          <button className="bg-gray-100 text-gray-600 px-6 py-2 rounded-md hover:bg-gray-200 transition-colors duration-200">
            Save as Draft
          </button>
        </div>
        )}
      </div>

      {/* Add Contacts Modal */}
      <AddContactsModal
        isOpen={isAddContactsModalOpen}
        onClose={() => setIsAddContactsModalOpen(false)}
        onSave={handleAudienceContacts}
      />

      {/* Import Contacts Modal */}
      <ImportContactsModal
        isOpen={isImportContactsModalOpen}
        onClose={() => setIsImportContactsModalOpen(false)}
        onSave={handleAudienceContacts}
      />

      <PersonalizeModal
        isOpen={isPersonalizeModalOpen}
        onClose={() => setIsPersonalizeModalOpen(false)}
        onSave={handlePersonalizeSave}
      />

      {/* All Contacts Modal */}
      <AllContactsModal
        isOpen={isAllContactsModalOpen}
        onClose={() => setIsAllContactsModalOpen(false)}
        onSave={handleAudienceContacts}
      />

      {/* Groups Modal */}
      <GroupsModal
        isOpen={isGroupsModalOpen}
        onClose={() => setIsGroupsModalOpen(false)}
        onSave={handleAudienceContacts}
      />

      {/* Status Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white shadow-xl">
            <div className="flex flex-col items-center px-6 py-6 text-center">
              <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full ${modal.type === 'success' ? 'bg-green-100' : 'bg-red-100'}`}>
                {modal.type === 'success' ? (
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                )}
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{modal.title}</h3>
              <p className="mt-2 text-sm text-gray-600">{modal.message}</p>
              {modal.subMessage && (
                <p className="mt-1 text-sm text-red-600">{modal.subMessage}</p>
              )}
            </div>
            <div className="flex justify-center border-t border-gray-100 px-6 py-4">
              <button
                onClick={() => setModal(null)}
                className="rounded-md bg-[#FF5722] px-6 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-[#F4511E]"
              >
                {modal.type === 'success' ? 'Done' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </SMSPageLayout>
  );
};

export default SMSCampaignPage; 